import { VuexModule, Module, Action, Mutation, getModule } from 'vuex-module-decorators'
import { getToken, setToken, removeToken, getRememberMe, removeRememberedUsername, removeRememberedPassword } from '@/utils/storage'
import { resetRouter } from '@/router'
// import { PermissionModule } from './permission'
// import { TagsViewModule } from './tags-view'
import store from '@/store'
import { IAllProjectInfo, IAllRoleInfo, IAllUserInfo, IOrg, IProject, IUser, IUserDeptInfo } from '@/api/types'
import { GetProfile, GetUserDepts, GetUserInfo, GetUserPermissions, GetUserRoles, LoginWithPassword, LogOut, SwitchOrg, GetUserProjects } from '@/api/user'

export interface IUserState {
  token: string
  userInfo: IUser | null
  roles: IAllRoleInfo[]
  permissions: string[]
  hasInitedPermission :boolean
  orgs: IOrg[]
  currentOrg: IOrg | null
  depts: IUserDeptInfo[]
  projects: IProject[]
  allInfo: IAllUserInfo | null
}

@Module({ dynamic: true, store, name: 'user' })
class User extends VuexModule implements IUserState {
  public token = getToken() || ''
  public roles: IAllRoleInfo[] = []
  public userInfo: IUser | null = null
  public permissions: string[] = []
  public hasInitedPermission = false
  public orgs: IOrg[] = []
  public currentOrg: IOrg | null = null
  public depts: IUserDeptInfo[] = []
  public projects: IProject[] = []
  public allInfo: IAllUserInfo | null = null

  @Mutation
  private SET_ALL_INFO(allInfo: IAllUserInfo | null) {
    this.allInfo = allInfo
  }

  @Mutation
  private SET_TOKEN(token: string) {
    this.token = token
  }

  @Mutation
  private SET_USER(userInfo: IUser|null) {
    this.userInfo = userInfo
  }

  @Mutation
  private SET_ROLES(roles: IAllRoleInfo[]) {
    this.roles = roles
  }

  @Mutation
  private SET_PREMISSIONS(per: string[]) {
    this.permissions = per
  }

  @Mutation
  private SET_HAS_INITED_PERMISSION(val: boolean) {
    this.hasInitedPermission = val
  }

  @Mutation
  private SET_ORGS(orgs: IOrg[]) {
    this.orgs = orgs
  }

  @Mutation
  private SET_CURRENT_ORG(org : IOrg|null) {
    this.currentOrg = org
  }

  @Mutation
  private SET_DEPTS(depts: IUserDeptInfo[]) {
    this.depts = depts
  }

  @Mutation
  private SET_PROJECTS(projs: IProject[]) {
    this.projects = projs
  }

  @Action
  public hasPermissions(per: string[]): boolean {
    return per.some(p => this.permissions.includes(p))
  }

  @Action
  public async LoginWithPassword(loginInfo: {
    username: string
    password: string
  }): Promise<string> {
    const { username, password } = loginInfo
    const trimmedUsername = username.trim()
    // console.log('LoginWithPassword', trimmedUsername, password)
    const { data } = await LoginWithPassword({ username: trimmedUsername, password })
    setToken(data.token)
    this.context.commit('SET_TOKEN', data.token)
    const ret = await this.processAllUserInfo(data.allInfo)
    if (ret > 1) {
      return '/select_org'
    } else if (ret === 0) {
      return '/profile'
    }
    return '/dashboard'
  }

  @Action
  public async LoginWithToken(): Promise<boolean> {
    try {
      const { data } = await GetUserInfo()
      this.processAllUserInfo(data)
      return true
    } catch (err) {
      // console.log('LoginWithToken error', err)
      this.ResetToken()
      return false
    }
  }

  @Action
  private processAllUserInfo(data: any): number {
    let len = 0

    if (data) {
      this.context.commit('SET_ALL_INFO', data)
    }

    if (data.permissions) {
      this.context.commit('SET_PREMISSIONS', data.permissions)
    }
    if (data.roles) {
      this.context.commit('SET_ROLES', data.roles)
    }
    if (data.orgs) {
      len = data.orgs.length
      this.context.commit('SET_ORGS', data.orgs)
    }
    if (data.depts) {
      this.context.commit('SET_DEPTS', data.depts)
    }
    if (data.user) {
      this.context.commit('SET_USER', data.user)
    }
    if (data.currentOrg) {
      this.context.commit('SET_CURRENT_ORG', data.currentOrg)
    }
    if (data.projects) {
      this.context.commit('SET_PROJECTS', data.projects)
    }
    return len
  }

  @Action
  public async SwitchOrg(orgId: number): Promise<string> {
    if (orgId <= 0 || orgId === this.currentOrg?.id) {
      console.log("无需切换", orgId)
      return '/dashboard'
    }
    const { data } = await SwitchOrg({ orgId })
    setToken(data.token)
    this.context.commit('SET_TOKEN', data.token)

    this.processAllUserInfo(data.allInfo)

    return '/dashboard'
  }

  @Action
  public ResetToken() {
    removeToken()
    this.resetUserInfo()
  }

  @Action
  public InitPermissionFlag() {
    this.context.commit('SET_HAS_INITED_PERMISSION', true)
  }

  get isLoggedIn(): boolean {
    return this.userInfo !== null
  }

  get useToLoggedIn(): boolean {
    return this.token !== ''
  }

  get isGod() : boolean {
    return this.permissions.includes('上帝')
  }

  // @Action
  // public AlreadySet(): boolean {
  //   return this.token !== ''
  // }



  @Action
  public async UpdateUserInfo() {
    if (this.token === '') {
      throw Error('GetUserInfo: token is undefined!')
    }
    const { data } = await GetProfile()
    if (!data) {
      throw Error('Verification failed, please Login again.')
    }

    this.context.commit('SET_USER', data)
  }

  @Action
  public async UpdateUserRoles() {
    if (this.token === '') {
      throw Error('GetUserRoles: token is undefined!')
    }

    if (this.currentOrg) {
      const { data } = await GetUserRoles()
      if (data) {
        this.context.commit('SET_ROLES', data)
      }
    }

  }

  @Action
  public async UpdateUserPermissions() {
    if (this.token === '') {
      throw Error('GetUserPermissions: token is undefined!')
    }
    if (this.currentOrg) {
      const { data } = await GetUserPermissions()
        if (data) {
        this.context.commit('SET_PREMISSIONS', data)
      }
    }
  }

  @Action
  public async UpdateUserDepts() {
    if (this.token === '') {
      throw Error('GetUserDepts: token is undefined!')
    }
    if (this.currentOrg) {
      const { data } = await GetUserDepts()
      if (data) {
        this.context.commit('SET_DEPTS', data)
      }
    }
  }

  @Action
  public async UpdateUserProjects() {
    if (this.token === '') {
      throw Error('GetUserProjects: token is undefined!')
    }
    if (this.currentOrg) {
      const { data } = await GetUserProjects()
      if (data) {
        this.context.commit('SET_PROJECTS', data)
      }
    }
  }

  // @Action
  // public async ChangeRoles(role: string) {
  //   // Dynamically modify permissions
  //   const token = role + '-token'
  //   this.context.commit('SET_TOKEN', token)
  //   setToken(token)
  //   await this.GetUserInfo()
  //   resetRouter()
  //   // Generate dynamic accessible routes based on roles
  //   PermissionModule.GenerateRoutes(this.roles)
  //   // Add generated routes
  //   PermissionModule.dynamicRoutes.forEach(route => {
  //     router.addRoute(route)
  //   })
  //   // Reset visited views and cached views
  //   TagsViewModule.delAllViews()
  // }

  @Action
  public async LogOut() {
    // 使用context来访问状态，而不是直接使用this.token
    if (this.token === '') {
      return // 如果token不存在，直接返回而不是抛出错误
    }
    await LogOut()
    removeToken()
    resetRouter()

    // 如果没有选择记住账号密码，则清除存储的凭据
    if (!getRememberMe()) {
      removeRememberedUsername()
      removeRememberedPassword()
    }

    // Reset visited views and cached views
    // TagsViewModule.delAllViews()
    // 使用context.commit来提交mutation，而不是直接调用mutation方法
    this.resetUserInfo()
  }
  @Action
  private resetUserInfo() {
    this.context.commit('SET_ALL_INFO', null)
    this.context.commit('SET_TOKEN', '')
    this.context.commit('SET_ROLES', [])
    this.context.commit('SET_USER', null)
    this.context.commit('SET_PREMISSIONS', [])
    this.context.commit('SET_HAS_INITED_PERMISSION', false)
    this.context.commit('SET_ORGS', [])
    this.context.commit('SET_CURRENT_ORG', null)
    this.context.commit('SET_DEPTS', [])
    this.context.commit('SET_PROJECTS', [])
  }
}


export const UserModule = getModule(User)
